import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface ReportTable {
    columns: string[];
    rows: (string | number)[][];
}

interface Props {
    title: string;
    description?: string;
    /** Shown instead of the chart when every value in the window is zero. */
    empty?: boolean;
    /** The same numbers as a table, for a screen reader or a careful reader. */
    table?: ReportTable;
    children: ReactNode;
    className?: string;
}

/** One chart, its title, and the numbers behind it. */
export default function ReportChartCard({
    title,
    description,
    empty = false,
    table,
    children,
    className = "",
}: Props) {
    const { t } = useTranslation();

    return (
        <section
            className={`flex flex-col rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] ${className}`}
        >
            <h3 className="text-base font-semibold text-content-primary">{title}</h3>
            {description && <p className="mt-0.5 text-sm text-content-secondary">{description}</p>}

            <div className="mt-4 flex-1">
                {empty ? (
                    <p className="flex h-full min-h-[120px] items-center justify-center rounded-xl bg-surface-sunken text-sm text-content-tertiary">
                        {t("center.reports.empty")}
                    </p>
                ) : (
                    children
                )}
            </div>

            {table && !empty && table.rows.length > 0 && (
                <details className="group mt-3 text-sm">
                    <summary className="cursor-pointer select-none text-xs font-medium text-content-secondary hover:text-content-primary">
                        {t("center.reports.showTable")}
                    </summary>
                    <div className="mt-2 max-h-64 overflow-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
                                    {table.columns.map((column) => (
                                        <th key={column} className="py-2 pr-4 font-medium">
                                            {column}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {table.rows.map((row, index) => (
                                    <tr
                                        key={index}
                                        className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                                    >
                                        {row.map((cell, cellIndex) => (
                                            <td
                                                key={cellIndex}
                                                className="py-2 pr-4 tabular-nums text-content-primary"
                                            >
                                                {cell}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
            )}
        </section>
    );
}
